/*******************************************************
 * MobileSelectDrawer Component
 *
 * Bottom sheet drawer for mobile FormSelect usage.
 * Replaces the standard MUI Autocomplete dropdown on
 * phone screens where the virtual keyboard would
 * otherwise consume most of the viewport.
 *******************************************************/
'use client';
//########## IMPORTS ##########
// #region IMPORTS
import { useState, useEffect, useCallback } from 'react';
import {
    SwipeableDrawer,
    Box,
    TextField,
    List,
    ListSubheader,
    ListItemButton,
    ListItemText,
    Typography,
    Divider,
    CircularProgress,
    InputAdornment,
    IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { getMobileDrawerZIndex, getMobileSelectAction, type MobileSelectMode } from './mobileSelectDrawerUtils';
import { tokens } from '@/src/theme/tokens';
// #endregion


//########## HOOKS ##########
// #region HOOKS
/** Tracks the visual viewport height to account for the virtual keyboard on mobile.
 *  Listens to both resize AND scroll on visualViewport (iOS fires scroll for keyboard offset),
 *  and polls via rAF for ~500ms on activation to catch keyboard animation on first open. */
function useVisualViewportHeight(active: boolean): number {
    const getFallback = () => (typeof window !== 'undefined' ? window.innerHeight : 800);

    const [height, setHeight] = useState<number>(() => {
        if (typeof window !== 'undefined' && window.visualViewport) {
            return window.visualViewport.height;
        }
        return getFallback();
    });

    const syncHeight = useCallback(() => {
        if (window.visualViewport) {
            setHeight(window.visualViewport.height);
        }
    }, []);

    useEffect(() => {
        if (!active || typeof window === 'undefined') return;

        const vv = window.visualViewport;
        if (!vv) return;

        // Immediate read
        setHeight(vv.height);

        // Listen to both resize AND scroll (iOS fires scroll for keyboard)
        vv.addEventListener('resize', syncHeight);
        vv.addEventListener('scroll', syncHeight);

        // Polling burst: catch keyboard animation over ~500ms
        let rafId: number;
        let pollCount = 0;
        const maxPolls = 30; // ~500ms at 60fps
        const poll = () => {
            if (vv) setHeight(vv.height);
            pollCount++;
            if (pollCount < maxPolls) {
                rafId = requestAnimationFrame(poll);
            }
        };
        rafId = requestAnimationFrame(poll);

        return () => {
            vv.removeEventListener('resize', syncHeight);
            vv.removeEventListener('scroll', syncHeight);
            cancelAnimationFrame(rafId);
        };
    }, [active, syncHeight]);

    return height;
}
// #endregion


//########## TYPE DEFINITIONS ##########
// #region TYPE DEFINITIONS
export interface MobileSelectDrawerProps<T> {
    open: boolean;
    onClose: () => void;
    onOpen: () => void;
    inputValue: string;
    onInputChange: (value: string) => void;
    label?: string;
    placeholder?: string;
    options: T[];
    getOptionLabel: (option: T) => string;
    getOptionKey?: (option: T) => string | number;
    isOptionEqualToValue?: (option: T, value: T) => boolean;
    groupBy?: (option: T) => string;
    getOptionSecondaryText?: (option: T) => string | null | undefined;
    value: T | null;
    onChange: (option: T | null) => void;
    loading?: boolean;
    disableClearable?: boolean;
    /** 'select' (default): toggle-deselect or select+close. 'add': call onChange, clear search, stay open. */
    mode?: MobileSelectMode;
}
// #endregion


//########## COMPONENT ##########
// #region COMPONENT
export default function MobileSelectDrawer<T>({
    open,
    onClose,
    onOpen,
    inputValue,
    onInputChange,
    label,
    placeholder,
    options,
    getOptionLabel,
    getOptionKey,
    isOptionEqualToValue,
    groupBy,
    getOptionSecondaryText,
    value,
    onChange,
    loading = false,
    disableClearable = false,
    mode
}: MobileSelectDrawerProps<T>) {
    const resolvedMode = mode ?? 'select';

    const viewportHeight = useVisualViewportHeight(open);
    const innerHeight = typeof window !== 'undefined' ? window.innerHeight : viewportHeight;
    const keyboardOffset = Math.max(0, innerHeight - viewportHeight);
    const HEADER_HEIGHT = 64;
    const maxDrawerHeight = Math.min(viewportHeight * 0.95, viewportHeight - HEADER_HEIGHT) + keyboardOffset;

    const handleSelect = (option: T) => {
        const action = getMobileSelectAction({
            option,
            isSelected: isSelected(option),
            disableClearable,
            mode: resolvedMode,
        });

        if (action.nextValue !== undefined) {
            onChange(action.nextValue);
        }
        if (action.shouldClearSearch) {
            onInputChange('');
        }
        if (action.shouldClose) {
            onClose();
        }
    };

    const isSelected = (option: T): boolean => {
        if (!value) return false;
        if (isOptionEqualToValue) return isOptionEqualToValue(option, value);
        if (getOptionKey) {
            const optionKey = getOptionKey(option);
            const valueKey = getOptionKey(value);
            if (optionKey == null || valueKey == null) return false;
            return optionKey === valueKey;
        }
        return getOptionLabel(option) === getOptionLabel(value);
    };

    // Pin selected record at top of list when applicable.
    const displayOptions = (() => {
        if (!value) return options;
        const searchTerm = inputValue.toLowerCase().trim();
        const selectedLabel = getOptionLabel(value).toLowerCase();
        // If searching and the selected record doesn't match, don't pin it
        if (searchTerm && !selectedLabel.includes(searchTerm)) return options;
        // Prepend selected value and filter it out of the rest to avoid duplicates
        const rest = options.filter(option => !isSelected(option));
        return [value, ...rest];
    })();

    const displayGroups = (() => {
        if (!groupBy) {
            return [{ group: '', options: displayOptions }];
        }

        const groups: Array<{ group: string; options: T[] }> = [];
        const groupMap = new Map<string, { group: string; options: T[] }>();

        displayOptions.forEach((option) => {
            const group = groupBy(option);
            let record = groupMap.get(group);
            if (!record) {
                record = { group, options: [] };
                groupMap.set(group, record);
                groups.push(record);
            }
            record.options.push(option);
        });

        return groups;
    })();

    return (
        <SwipeableDrawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            onOpen={onOpen}
            disableSwipeToOpen
            sx={(theme) => ({
                zIndex: getMobileDrawerZIndex(theme.zIndex),
            })}
            PaperProps={{
                sx: {
                    maxHeight: maxDrawerHeight,
                    borderRadius: `${tokens.radius.sheet} ${tokens.radius.sheet} 0 0`,
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            {/* Drag handle + close button */}
            <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', pt: 1.5, pb: 1 }}>
                <Box sx={{
                    width: 36,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: 'grey.700'
                }} />
                <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 4 }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Label */}
            {label && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600, px: 2, pb: 1 }}>
                    {label}
                </Typography>
            )}

            {/* Search field */}
            <Box sx={{ px: 2, pb: 1.5 }}>
                <TextField
                    fullWidth
                    autoFocus
                    size="small"
                    placeholder={placeholder || 'Search...'}
                    value={inputValue}
                    onChange={(e) => onInputChange(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            )
                        }
                    }}
                />
            </Box>

            <Divider />

            {/* Scrollable options list */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : displayOptions.length === 0 ? (
                    <Typography sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                        No options
                    </Typography>
                ) : (
                    <List disablePadding>
                        {displayGroups.map(({ group, options: groupOptions }) => (
                            <Box component="li" key={group || 'ungrouped'} sx={{ listStyle: 'none' }}>
                                {group && (
                                    <ListSubheader
                                        component="div"
                                        disableSticky
                                        sx={{
                                            lineHeight: '32px',
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            bgcolor: 'background.paper',
                                        }}
                                    >
                                        {group}
                                    </ListSubheader>
                                )}
                                {groupOptions.map((option, index) => {
                                    const optionLabel = getOptionLabel(option);
                                    const selected = isSelected(option);
                                    const rawKey = getOptionKey ? getOptionKey(option) : undefined;
                                    const key = rawKey != null ? String(rawKey) : `${group}-${index}-${optionLabel}`;
                                    const secondaryText = getOptionSecondaryText?.(option) || undefined;

                                    return (
                                        <ListItemButton
                                            key={key}
                                            selected={selected}
                                            onClick={() => handleSelect(option)}
                                            sx={{ minHeight: secondaryText ? 64 : 48 }}
                                        >
                                            <ListItemText primary={optionLabel} secondary={secondaryText} />
                                            {selected && <CheckIcon color="primary" fontSize="small" />}
                                        </ListItemButton>
                                    );
                                })}
                            </Box>
                        ))}
                    </List>
                )}
            </Box>

            {/* Keyboard spacer — pushes visible content above virtual keyboard */}
            {keyboardOffset > 0 && (
                <Box sx={{ minHeight: keyboardOffset, flexShrink: 0 }} />
            )}
        </SwipeableDrawer>
    );
}
// #endregion
